import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Zap, KanbanSquare, Megaphone, Sparkles, MapPin, Check } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1676210134050-6f12c6898395?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHw0fHxwbHVtYmVyJTIwcHJvZmVzc2lvbmFsJTIwd29ya2luZ3xlbnwwfHx8fDE3ODQ1NjU1OTZ8MA&ixlib=rb-4.1.0&q=85";
const FEATURE_IMG = "https://images.unsplash.com/photo-1739203469638-d6f54c24a5da?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA6MDV8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBodmFjJTIwaW5zdGFsbGF0aW9uJTIwdGVhbXxlbnwwfHx8fDE3ODQ1NjU1OTZ8MA&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  return (
    <div className="min-h-screen bg-zinc-50 text-zinc-900">
      {/* Nav */}
      <header className="sticky top-0 z-50 backdrop-blur-md bg-zinc-50/80 border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-md bg-[#FF5A00] flex items-center justify-center">
              <Zap className="w-4 h-4 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-heading font-bold text-lg">LeadForge</span>
          </div>
          <nav className="hidden md:flex items-center gap-8 text-sm text-zinc-600">
            <a href="#features" className="hover:text-zinc-900">Features</a>
            <a href="#pipeline" className="hover:text-zinc-900">Pipeline</a>
            <a href="#campaigns" className="hover:text-zinc-900">Campaigns</a>
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/login" data-testid="nav-login-link" className="text-sm font-medium hover:text-[#FF5A00]">Log in</Link>
            <Link to="/request-access" data-testid="nav-request-access-link">
              <Button className="bg-zinc-900 hover:bg-zinc-800 text-white btn-lift">Request access <ArrowRight className="w-4 h-4 ml-1"/></Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 pt-20 pb-24 grid lg:grid-cols-2 gap-16 items-center">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-orange-50 border border-orange-200 rounded-full text-xs font-medium text-[#E04F00] mb-6">
            <Sparkles className="w-3.5 h-3.5" />
            Private beta · Invite only
          </div>
          <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-black tracking-tight leading-[0.95]">
            Every job starts with a <span className="hero-gradient-text">lead you didn't drop</span>.
          </h1>
          <p className="mt-6 text-lg text-zinc-600 max-w-xl">
            LeadForge is the operations OS for plumbers, HVAC crews, roofers, and landscapers.
            Capture, score, and close every lead — with AI writing your follow-ups.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/request-access" data-testid="hero-cta-request-access">
              <Button size="lg" className="bg-[#FF5A00] hover:bg-[#E04F00] text-white text-base h-12 px-6 btn-lift">
                Request access <ArrowRight className="w-4 h-4 ml-2"/>
              </Button>
            </Link>
            <Link to="/login">
              <Button variant="outline" size="lg" className="h-12 px-6 border-zinc-300">I already have an account</Button>
            </Link>
          </div>
          <div className="mt-10 flex items-center gap-6 text-sm text-zinc-500">
            <div className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-600"/> Multi-business</div>
            <div className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-600"/> AI follow-ups</div>
            <div className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-600"/> Campaign attribution</div>
          </div>
        </div>

        <div className="relative">
          <div className="absolute -inset-6 bg-gradient-to-br from-orange-100/60 to-transparent rounded-3xl blur-2xl"></div>
          <div className="relative rounded-2xl overflow-hidden card-crisp border border-zinc-200">
            <img src={HERO_IMG} alt="Home service pro" className="w-full h-[520px] object-cover" />
            <div className="absolute bottom-4 left-4 right-4 flex items-center gap-3 bg-white/95 backdrop-blur border border-zinc-200 rounded-xl p-4 shadow-lg">
              <div className="w-10 h-10 rounded-lg bg-orange-50 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-[#FF5A00]" />
              </div>
              <div className="flex-1">
                <div className="text-xs text-zinc-500 font-mono-accent uppercase tracking-wide">AI score</div>
                <div className="font-heading font-bold">Sarah M. · 92/100 · High intent</div>
              </div>
              <span className="px-2 py-1 text-xs bg-emerald-100 text-emerald-800 rounded-md font-medium">Booked</span>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="max-w-7xl mx-auto px-6 py-20">
        <div className="max-w-2xl mb-16">
          <div className="text-xs font-mono-accent uppercase tracking-widest text-[#FF5A00] mb-3">Built for the trades</div>
          <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight">Your leads. Organized. Automated.</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { icon: KanbanSquare, title: "7-stage pipeline", body: "New Lead → Contacted → Quote → Follow-up → Booked → Completed. Drag between columns." },
            { icon: Sparkles, title: "AI follow-ups", body: "Gemini-drafted SMS & email in seconds. Every lead gets a score & recommended action." },
            { icon: Megaphone, title: "Attributable campaigns", body: "SMS, email, direct mail. Tag every lead's source and see what actually paid off." },
          ].map((f) => (
            <div key={f.title} className="bg-white border border-zinc-200 rounded-xl p-6 card-crisp hover:-translate-y-1 transition-transform">
              <f.icon className="w-6 h-6 text-[#FF5A00] mb-4" />
              <h3 className="font-heading font-bold text-xl mb-2">{f.title}</h3>
              <p className="text-zinc-600 text-sm leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Multi-business */}
      <section id="pipeline" className="max-w-7xl mx-auto px-6 py-20 grid lg:grid-cols-2 gap-16 items-center">
        <div className="rounded-2xl overflow-hidden border border-zinc-200 card-crisp">
          <img src={FEATURE_IMG} alt="HVAC team" className="w-full h-[440px] object-cover" />
        </div>
        <div>
          <div className="text-xs font-mono-accent uppercase tracking-widest text-[#FF5A00] mb-3">Multi-tenant SaaS</div>
          <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight leading-tight">
            One platform.<br/>Every crew you own.
          </h2>
          <p className="mt-6 text-lg text-zinc-600">
            Each business gets its own workspace, leads, campaigns, and settings. Perfect for holding companies, franchise owners, and multi-service operators.
          </p>
          <div className="mt-8 space-y-3">
            {["Isolated data per business", "Custom service area & type", "Team-ready (invite reps coming)"].map((t) => (
              <div key={t} className="flex items-center gap-3 text-zinc-700">
                <div className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center">
                  <Check className="w-3 h-3 text-emerald-700"/>
                </div>
                {t}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section id="campaigns" className="bg-zinc-950 text-white py-20 relative overflow-hidden grain">
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <MapPin className="w-6 h-6 text-[#FF5A00] mx-auto mb-4" />
          <h2 className="font-heading text-4xl md:text-6xl font-black tracking-tight">Ready to stop losing leads?</h2>
          <p className="mt-6 text-zinc-400 text-lg">We're onboarding a handful of operators by hand. Tell us about your business.</p>
          <div className="mt-10">
            <Link to="/request-access">
              <Button size="lg" className="bg-[#FF5A00] hover:bg-[#E04F00] text-white h-14 px-8 text-base btn-lift" data-testid="footer-cta-request-access">
                Request beta access <ArrowRight className="w-4 h-4 ml-2"/>
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <footer className="max-w-7xl mx-auto px-6 py-10 text-sm text-zinc-500 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded bg-[#FF5A00] flex items-center justify-center">
            <Zap className="w-3.5 h-3.5 text-white" />
          </div>
          <span className="font-heading font-bold text-zinc-700">LeadForge</span>
        </div>
        <div>© 2026 LeadForge Ops</div>
      </footer>
    </div>
  );
}
